import React from 'react';
import clsx from 'clsx';
import * as Headless from '@headlessui/react';
import { Await, TransitionDiv } from '@oysterjs/ui/async';
import {
  Form,
  Link,
  useActionData,
  useLoaderData,
  useNavigate,
  useNavigation,
  useOutlet,
  useSubmit
} from 'react-router-dom';
import {
  AccountCertificateOfLiabilityInsuranceFieldsFragment,
  AccountFieldsFragment,
  CertificateOfLiabilityInsuranceFieldsFragment,
  ClientCreateCertificateInput,
  ClientCreateCertificateMutation,
  ClientCreateCertificateResultCode,
  CreateCertificateOptionsFieldsFragment
} from '../api/graphql';
import { Button } from '@oysterjs/ui/button';
import { Listbox } from '@oysterjs/ui/listbox';
import { MountableSlideout } from '@oysterjs/ui/slideout';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Description, ErrorMessage, Field, FieldGroup, Label, Legend } from '@oysterjs/ui/fieldset';
import { Fieldset } from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { AddressForm } from '@oysterjs/ui/address';
import { Textarea } from '@oysterjs/ui/textarea';
import { Text } from '@oysterjs/ui/text';
import { Radio, RadioField, RadioGroup } from '@oysterjs/ui/radio';
import { ValidationError } from '@oysterjs/core/graphql';
import { ActionResult } from '@oysterjs/core/graphql/actions';
import { useFormActions } from '@oysterjs/core/graphql/form';
import { AnimatePresence } from 'motion/react';
import { Spinner } from '@oysterjs/ui/spinner';

export const CertificatesPage = () => {
  const outlet = useOutlet();
  const loader = useLoaderData() as
    | {
        data: Promise<{
          account: AccountFieldsFragment & AccountCertificateOfLiabilityInsuranceFieldsFragment;
        }>;
      }
    | undefined;

  return (
    <>
      <React.Suspense
        fallback={
          <TransitionDiv className="my-16 flex items-center justify-center">
            <div className="rounded-lg bg-white p-10 shadow">
              <Spinner size="size-10" color="neutral" />
            </div>
          </TransitionDiv>
        }
      >
        <Await resolve={loader?.data}>
          {(data) =>
            data && (
              <div className="rounded-lg bg-white px-5 py-6 shadow sm:px-6">
                <h2 className="text-xl font-semibold mt-0 mb-2">Your Certificates</h2>
                <Certificates certificates={data.account.certificatesOfLiabilityInsurance} />
              </div>
            )
          }
        </Await>
      </React.Suspense>
      <AnimatePresence mode="wait">{outlet && React.cloneElement(outlet, {})}</AnimatePresence>
    </>
  );
};

export const Certificates = ({
  certificates
}: {
  certificates: CertificateOfLiabilityInsuranceFieldsFragment[];
}) => {
  if (certificates.length === 0) {
    return (
      <p className="text-sm text-neutral-500">
        No certificates found.{' '}
        <Link to="new" className="text-primary-600 hover:underline hover:text-primary-900">
          Create one.
        </Link>
      </p>
    );
  }

  return (
    <>
      <p className="text-sm text-neutral-500">
        <Link to="new" className="text-primary-600 hover:underline hover:text-primary-900">
          Create a new certificate
        </Link>
      </p>
      <ul role="list" className="divide-y divide-gray-100 mt-2 flex flex-col gap-2">
        {certificates.map((certificate) => (
          <li key={certificate.id}>
            <div className="flex-grow mt-2">
              <a
                href={certificate.downloadUrl}
                target="_blank"
                className="text-base/6 truncate font-medium text-neutral-900 hover:underline"
              >
                {certificate.holder.name}
              </a>
              <p className="text-sm truncate  text-neutral-500">
                {certificate.holder.address.city}, {certificate.holder.address.zone}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </>
  );
};

export const CreateCertificatePage = () => {
  const navigate = useNavigate();
  const loader = useLoaderData() as
    | {
        data: Promise<{
          createCertificateOptions: CreateCertificateOptionsFieldsFragment;
        }>;
      }
    | undefined;

  return (
    <MountableSlideout onClose={() => navigate(-1)} title="Create Certificate">
      <React.Suspense fallback={<></>}>
        <Await resolve={loader?.data}>
          {(data) =>
            data?.createCertificateOptions && (
              <CreateCertificate createCertificateOptions={data.createCertificateOptions} />
            )
          }
        </Await>
      </React.Suspense>
    </MountableSlideout>
  );
};

const CreateCertificate = (props: {
  createCertificateOptions: CreateCertificateOptionsFieldsFragment;
}) => {
  const navigation = useNavigation();
  const navigate = useNavigate();
  const submit = useSubmit();
  const action = useActionData() as ActionResult<ClientCreateCertificateMutation>;

  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);
  const [form, setForm] = React.useState<ClientCreateCertificateInput>({
    holder: {
      name: '',
      address: {
        line1: '',
        line2: '',
        city: '',
        zone: '',
        postalCode: ''
      }
    },
    isAdditionalInsured: false,
    notes: ''
  });
  const { updateForm, updateFormMulti, getValidationError } = useFormActions(
    setForm,
    setValidationErrors
  );

  const canHaveAdditionalInsured =
    props.createCertificateOptions.allowedBlanketAdditionalInsureds.length +
      props.createCertificateOptions.allowedNamedAdditionalInsureds.length >
    0;

  const isNamedInsuredSelected =
    !!props.createCertificateOptions.allowedNamedAdditionalInsureds.find(
      (ai) => ai.id === form.additionalInsuredId
    );

  const getAdditionalInsured = (id: string) => {
    return (
      props.createCertificateOptions.allowedNamedAdditionalInsureds.find((ai) => ai.id === id) ||
      props.createCertificateOptions.allowedBlanketAdditionalInsureds.find((ai) => ai.id === id)
    );
  };

  React.useEffect(() => {
    if (action?.validationErrors) {
      setValidationErrors(action.validationErrors);
    }
  }, [action?.validationErrors]);

  React.useEffect(() => {
    switch (action?.data?.clientCreateCertificate?.code) {
      case ClientCreateCertificateResultCode.Success:
        window.open(action?.data?.clientCreateCertificate?.certificate?.downloadUrl, '_blank');
        navigate(-1);
        break;

      case ClientCreateCertificateResultCode.RequiresEndorsement:
      case ClientCreateCertificateResultCode.RequiresReview:
        // Display a message based on the error code and offer a button to go to the service request
        break;

      case ClientCreateCertificateResultCode.Error:
        // Display an error message
        break;

      default:
        // Display an error message
        break;
    }
  }, [JSON.stringify(action)]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (!form) {
      return;
    }

    e.preventDefault();
    submit(form, { method: 'POST', encType: 'application/json' });
  };

  if (
    action?.data?.clientCreateCertificate?.code ===
      ClientCreateCertificateResultCode.RequiresEndorsement ||
    action?.data?.clientCreateCertificate?.code === ClientCreateCertificateResultCode.RequiresReview
  ) {
    return (
      <div>
        <h1 className="mt-0 text-2xl font-semibold">Your certificate is pending review</h1>
        <p className="text-sm text-neutral-700">
          Since you indicated that your certificate requires an additional insured to be listed
          which is not explicitly listed on your policy, your certificate request must be reviewed
          by your agent. In some cases, this might require a change to your policy or follow up
          questions. Your agent will reach out to you via email if this is necessary.
        </p>
        <Button
          color="white"
          className="mt-4"
          href={action?.data?.clientCreateCertificate?.serviceRequest?.id}
        >
          View Service Request
        </Button>
      </div>
    );
  }

  return (
    <Form onSubmit={handleSubmit}>
      <Fieldset>
        <Legend className="sm:text-sm/6">Is the certificate holder an additional insured?</Legend>
        <Text>
          Select whether the certificate holder is an additional insured. If the certificate holder
          is an additional insured, you'll need to specify the relationship between the certificate
          holder and the insured. This may require additional review by your agent.
        </Text>
        <RadioGroup
          value={form.isAdditionalInsured ? 'true' : 'false'}
          onChange={(val) =>
            updateForm(['isAdditionalInsured'], (prev) => ({
              ...prev,
              isAdditionalInsured: val === 'true',
              additionalInsuredId: null,
              isOtherAdditionalInsured: false,
              otherAdditionalInsuredRelationship: null
            }))
          }
        >
          <RadioField>
            <Radio value={'true'} color="primary" />
            <Label>Yes, the certificate holder needs to be an additional insured.</Label>
          </RadioField>
          <RadioField>
            <Radio value={'false'} color="primary" />
            <Label>No, the certificate holder just needs proof of coverage.</Label>
          </RadioField>
        </RadioGroup>

        {form.isAdditionalInsured && (
          <FieldGroup>
            {canHaveAdditionalInsured && (
              <Field>
                <Label>Relationship to Insured</Label>
                <Listbox
                  value={form.isOtherAdditionalInsured ? 'other' : form.additionalInsuredId || null}
                  placeholder="Choose one..."
                  onChange={(val) => {
                    updateForm(['additionalInsuredId'], (prev) => ({
                      ...prev,
                      isAdditionalInsured: true,
                      additionalInsuredId: val === 'other' ? null : val,
                      isOtherAdditionalInsured: val === 'other',
                      otherAdditionalInsuredRelationship: val === 'other' ? '' : null
                    }));

                    const additionalInsured = getAdditionalInsured(val || '');
                    if (!additionalInsured) {
                      return;
                    }

                    if (
                      additionalInsured.__typename === 'NamedAdditionalInsuredCertificateOption'
                    ) {
                      updateFormMulti(
                        [
                          ['name'],
                          ['address', 'line1'],
                          ['address', 'line2'],
                          ['address', 'city'],
                          ['address', 'zone'],
                          ['address', 'postalCode']
                        ],
                        (prev) => ({
                          ...prev,
                          holder: {
                            name: additionalInsured.name,
                            address: additionalInsured.address
                          }
                        })
                      );
                    } else {
                      updateFormMulti(
                        [
                          ['name'],
                          ['address', 'line1'],
                          ['address', 'line2'],
                          ['address', 'city'],
                          ['address', 'zone'],
                          ['address', 'postalCode']
                        ],
                        (prev) => ({
                          ...prev,
                          holder: {
                            name: '',
                            address: {
                              line1: '',
                              line2: '',
                              city: '',
                              zone: '',
                              postalCode: ''
                            }
                          }
                        })
                      );
                    }
                  }}
                  invalid={!!getValidationError(validationErrors, ['additionalInsuredId'])}
                  className="w-full truncate"
                >
                  {props.createCertificateOptions.allowedBlanketAdditionalInsureds.map((ai) => (
                    <AdditionalInsuredListOption
                      key={ai.id}
                      value={ai.id}
                      type="blanket"
                      title={ai.type}
                      description={ai.description}
                    />
                  ))}
                  {props.createCertificateOptions.allowedNamedAdditionalInsureds.map((ai) => (
                    <AdditionalInsuredListOption
                      key={ai.id}
                      value={ai.id}
                      type="named"
                      title={ai.name}
                      description={`${ai.address.city}, ${ai.address.zone}`}
                    />
                  ))}
                  <AdditionalInsuredListOption
                    type="other"
                    title="Other"
                    description="Select this option if the relationship is not listed above"
                    value="other"
                  />
                </Listbox>
                <ErrorMessage>
                  {getValidationError(validationErrors, ['additionalInsuredId'])}
                </ErrorMessage>
              </Field>
            )}
            {canHaveAdditionalInsured && form.isOtherAdditionalInsured && (
              <Field>
                <Label>Describe the relationship</Label>
                <Description>
                  Describe the relationship between the certificate holder and the insured.
                </Description>
                <Input
                  value={form.otherAdditionalInsuredRelationship || ''}
                  onChange={(e) => {
                    const val = e.currentTarget.value;
                    updateForm(['otherAdditionalInsuredRelationship'], (prev) => ({
                      ...prev,
                      otherAdditionalInsuredRelationship: val
                    }));
                  }}
                  invalid={
                    !!getValidationError(validationErrors, ['otherAdditionalInsuredRelationship'])
                  }
                />
                <ErrorMessage>
                  {getValidationError(validationErrors, ['otherAdditionalInsuredRelationship'])}
                </ErrorMessage>
              </Field>
            )}
            {canHaveAdditionalInsured && form.isOtherAdditionalInsured && (
              <div className="rounded-md bg-amber-50 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <ExclamationTriangleIcon
                      aria-hidden="true"
                      className="h-5 w-5 text-amber-600"
                    />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-sm font-medium text-amber-800 mt-0">
                      Additional Review Required
                    </h3>
                    <p className="mt-1 text-sm text-amber-900/80">
                      In order to make sure your policy extends coverage to this party, your agent
                      will perform a manual review. In most cases, you'll receive your certificate
                      within 24 hours, but the process may take up to several business days
                      depending on complexity. Your agent will follow up via email.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </FieldGroup>
        )}

        <FieldGroup>
          <Field>
            <Label>Certificate Holder</Label>
            <Description>
              Enter the name of the party that will receive and hold the certificate.
            </Description>
            <Input
              value={form.holder.name}
              disabled={isNamedInsuredSelected}
              placeholder="ACME Inc."
              onChange={(e) => {
                const val = e.currentTarget.value;
                updateForm(['holder', 'name'], (prev) => ({
                  ...prev,
                  holder: { ...prev.holder, name: val }
                }));
              }}
              invalid={!!getValidationError(validationErrors, ['holder', 'name'])}
            />
            <ErrorMessage>{getValidationError(validationErrors, ['holder', 'name'])}</ErrorMessage>
          </Field>
        </FieldGroup>

        <AddressForm
          disabled={isNamedInsuredSelected}
          addressTitle="Certificate Holder Address"
          addressDescription="Enter the address of the party that will receive and hold the certificate."
          initialValue={{
            line1: form.holder.address.line1,
            line2: form.holder.address.line2 || '',
            city: form.holder.address.city,
            zone: form.holder.address.zone,
            postalCode: form.holder.address.postalCode
          }}
          onChange={(update) =>
            updateFormMulti(
              [
                ['holder', 'address', 'line1'],
                ['holder', 'address', 'line2'],
                ['holder', 'address', 'city'],
                ['holder', 'address', 'zone'],
                ['holder', 'address', 'postalCode']
              ],
              (prev) => ({
                ...prev,
                holder: {
                  ...prev.holder,
                  address: update(prev.holder.address)
                }
              })
            )
          }
          validationErrors={{
            line1: getValidationError(validationErrors, ['holder', 'address', 'line1']),
            line2: getValidationError(validationErrors, ['holder', 'address', 'line2']),
            city: getValidationError(validationErrors, ['holder', 'address', 'city']),
            zone: getValidationError(validationErrors, ['holder', 'address', 'zone']),
            postalCode: getValidationError(validationErrors, ['holder', 'address', 'postalCode'])
          }}
        />
        <FieldGroup>
          <Field>
            <Label>Notes</Label>
            <Description>
              Enter any additional information that is relevant to the creation of the certificate.
            </Description>
            <Textarea
              value={form.notes}
              rows={4}
              onChange={(e) => {
                const val = e.currentTarget.value;
                updateForm(['notes'], (prev) => ({ ...prev, notes: val }));
              }}
            />
          </Field>
        </FieldGroup>
      </Fieldset>
      <Button
        type="submit"
        color="sky"
        className="mt-4"
        disabled={navigation.state === 'submitting'}
      >
        Request Certificate
      </Button>
    </Form>
  );
};

const AdditionalInsuredListOption = (props: {
  value: string;
  type: 'blanket' | 'named' | 'other';
  title: string;
  description: string;
}) => {
  const sharedClasses = clsx(
    // Base
    'flex flex-grow flex-col max-w-[26rem] col-start-2'
  );

  return (
    <Headless.ListboxOption as={React.Fragment} {...props}>
      {({ selectedOption }) => {
        if (selectedOption) {
          return (
            <div className={clsx(sharedClasses)}>
              <div className="truncate font-medium">{props.title}</div>
              <div className="flex flex-1 overflow-hidden text-neutral-500 group-data-[focus]/option:text-white dark:text-neutral-400">
                <span className="flex-1 truncate">{props.description}</span>
              </div>
            </div>
          );
        }

        return (
          <div
            className={clsx(
              // Basic layout
              'group/option grid cursor-pointer grid-cols-[theme(spacing.5),1fr] items-baseline gap-x-2 rounded-lg py-2.5 pl-2 pr-3.5 sm:grid-cols-[theme(spacing.4),1fr] sm:py-1.5 sm:pl-1.5 sm:pr-3',
              // Typography
              'text-base/6 text-neutral-950 sm:text-sm/6 dark:text-white forced-colors:text-[CanvasText]',
              // Focus
              'outline-none data-[focus]:bg-primary-500 data-[focus]:text-white',
              // Forced colors mode
              'forced-color-adjust-none forced-colors:data-[focus]:bg-[Highlight] forced-colors:data-[focus]:text-[HighlightText]',
              // Disabled
              'data-[disabled]:opacity-50'
            )}
          >
            <div className="self-start">
              <svg
                className="relative hidden size-5 self-baseline stroke-current group-data-[selected]/option:inline sm:size-4"
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M4 8.5l3 3L12 4"
                  strokeWidth={1.5}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className={clsx(sharedClasses)}>
              <div className="truncate font-medium">{props.title}</div>
              <div className="flex flex-1 overflow-hidden text-neutral-500 group-data-[focus]/option:text-white dark:text-neutral-400">
                <span className="flex-1">{props.description}</span>
              </div>
            </div>
          </div>
        );
      }}
    </Headless.ListboxOption>
  );
};
